import React, { useEffect, useMemo, useState } from 'react';
import { Column, useSortBy, useTable, usePagination, useGlobalFilter, useFilters, FilterValue } from 'react-table';
import ReactPaginate from 'react-paginate';
import { Col, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { MultiValue, SingleValue } from 'react-select';
import { Transactions } from '../../models/transactions-model';
import './transaction-details-component.scss';
import NoCollectiblesComponent from '../no-collectibles/no-collectibles-components';
import noImageFound from '../../assets/images/no-image-found.jpg';
import Tools from '../../tools';
import imageFormatter from './formatters/image-formatter';
import blockchainFormatter from './formatters/blockchain-formatter';
import shortTextFormatter from './formatters/short-text-formatter';
import amountFormatter from './formatters/amount-formatter';
import Blockchain from '../../enums/blockchain-enum';
import { TransactionRow } from '../../models/transaction-row-model';
import CollapsableRow from './collapsable-row/collapsable-row-component';
import SelectComponent, { SelectColors, SelectOption } from '../select/select-component';
import MultiSelectComponent from '../multi-select/multi-select-component';
import NoCollectiblesType from '../../enums/no-collectibles-type-enum';

// Define a default UI for filtering
interface GlobalFilterProps {
  globalFilter: string;
  setGlobalFilter: (filterValue: FilterValue) => void;
}

function GlobalFilter({ globalFilter, setGlobalFilter }: GlobalFilterProps) {
  return (
    <div className="d-flex search-input">
      <input
        className="form-control"
        value={globalFilter || ''}
        onChange={(e) => {
          setGlobalFilter(e.target.value);
        }}
        placeholder="Filter…"
      />
      <button type="button" className="btn btn-primary">
        <i className="icon-search4" />
      </button>
    </div>
  );
}

type TransactionDetailsComponentProps = {
  transactions: Transactions;
};

export default function TransactionDetailsComponent({ transactions }: TransactionDetailsComponentProps): JSX.Element {
  const [availableCollections, setAvailableCollections] = useState<{ [name: string]: string }>({});
  const [summaryText, setSummaryText] = useState('');
  const { txId, selectedAddress } = useParams();

  const data = useMemo(() => {
    const items: TransactionRow[] = transactions?.data?.map((t) => {
      return {
        blockchain: t.blockchain,
        collection: t.orbs?.[0]?.collection?.name,
        contract: t.contract?.address,
        destination: t.destination,
        destinationUsername: t.destination_user?.spellsofgenesis.username ?? '',
        explorer: transactions?.meta?.activeBlockchains
          ? transactions?.meta?.activeBlockchains[t.blockchain]?.explorerTx
          : '',
        image: t.orbs?.[0]?.imageUrl?.includes('cryptokitties')
          ? noImageFound
          : null ?? t.orbs?.[0]?.imageUrl ?? t.orbs?.[0]?.imgURL ?? t.orbs?.[0]?.fallbackImageUrl ?? noImageFound,
        quantity: `${t.source === selectedAddress ? '-' : ''}${
          t?.adaptedQuantity != null ? t?.adaptedQuantity.toString() : t.quantity
        }`,
        source: t.source,
        sourceUsername:
          t.source === '0x0000000000000000000000000000000000000000'
            ? 'issuance'
            : null ?? t.source_user?.spellsofgenesis.username ?? '',
        time: Tools.convertDate(t.timestamp),
        txId: t.txId,
        userAddress: selectedAddress,
      };
    });

    const collectionDict: { [name: string]: string } = {};
    const uniqueCollections = Tools.removeDuplicates(items.map((x) => x.collection)).sort();
    uniqueCollections.forEach((x) => {
      collectionDict[x] = x;
    });
    setAvailableCollections(collectionDict);

    return items;
  }, []);

  const columns = React.useMemo(
    () =>
      [
        {
          Header: 'Image',
          accessor: 'image',
          Cell: imageFormatter,
          disableSortBy: true,
        },
        {
          Header: 'Time',
          accessor: 'time',
        },
        {
          Header: 'Blockchain',
          accessor: 'blockchain',
          Cell: blockchainFormatter,
          filter: 'includesSome',
        },
        {
          Header: 'Collection',
          accessor: 'collection',
          filter: 'includesSome',
        },
        {
          Header: 'Contract',
          accessor: 'contract',
          Cell: shortTextFormatter,
        },
        {
          Header: 'From',
          accessor: 'source',
          Cell: shortTextFormatter,
        },
        {
          Header: 'To',
          accessor: 'destination',
          Cell: shortTextFormatter,
        },
        {
          Header: 'Quantity',
          accessor: 'quantity',
          Cell: amountFormatter,
        },
        {
          Header: 'Transaction',
          accessor: 'txId',
          isVisible: false,
        },
        {
          Header: 'Explorer',
          accessor: 'explorer',
          isVisible: false,
        },
        {
          Header: '',
          accessor: 'chevron',
          disableSortBy: true,
        },
      ] as Column[],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        hiddenColumns: ['txId'],
        pageIndex: 0,
        pageSize: 10,
      },
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    page,
    prepareRow,
    pageCount,
    setGlobalFilter,
    setFilter,
    gotoPage,
    rows,
    setPageSize,
    state: { globalFilter, pageIndex, pageSize, filters },
  } = tableInstance;

  useEffect(() => {
    if (txId) {
      setGlobalFilter(txId);
    }
  }, []);

  const blockchainEnums: { [name: string]: string } = {
    [Blockchain.Counterparty]: Tools.capitalizeFirstLetter(Blockchain.Counterparty),
    [Blockchain.Ethereum]: Tools.capitalizeFirstLetter(Blockchain.Ethereum),
    [Blockchain.Klaytn]: Tools.capitalizeFirstLetter(Blockchain.Klaytn),
  };

  const blockchainOptions = Object.keys(blockchainEnums).map((key) => ({
    label: blockchainEnums[key],
    value: key,
  }));

  const collectionOptions = Object.keys(availableCollections).map((key) => ({
    label: availableCollections[key],
    value: key,
  }));

  const onBlockchainFilter = (values: MultiValue<SelectOption>) => {
    setFilter(
      'blockchain',
      values.map((value) => value.value)
    );
  };

  const onCollectionFilter = (values: MultiValue<SelectOption>) => {
    setFilter(
      'collection',
      values.map((value) => value.value)
    );
  };

  const handlePageClick = ({ selected }: { selected: number }) => {
    gotoPage(selected);
  };

  const handlePageSizeChange = (value: SingleValue<SelectOption>) => {
    if (value?.value) {
      setPageSize(+value.value);
    }
  };

  useEffect(() => {
    const paginationSettings = {
      from: pageIndex * pageSize + 1,
      to: pageIndex * pageSize + page.length,
      of: rows.length,
      total: data.length,
    };
    setSummaryText(
      `Showing ${paginationSettings.from} to ${paginationSettings.to} of ${paginationSettings.of} entries
       ${filters.length || globalFilter ? `(filtered from ${paginationSettings.total} total entries)` : ''}`
    );
  }, [pageSize, pageIndex, page, data, rows]);

  const pageSizeOptions: SelectOption[] = [
    { value: '10', label: '10' },
    { value: '25', label: '25' },
    { value: '50', label: '50' },
    { value: '100', label: '100' },
  ];

  return data?.length ? (
    <div className="transactions-table">
      <Row className="filter-bar my-4">
        <Col xs={12} xl={11}>
          <Row>
            <Col xs={12} xl={6} className="mb-3">
              <GlobalFilter globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
            </Col>
            <Col xs={12} xl={6}>
              <Row>
                <Col xs={12} md={6} className="mb-3">
                  <MultiSelectComponent
                    placeholder="Select Blockchain(s)…"
                    options={blockchainOptions}
                    onChange={onBlockchainFilter}
                    values={
                      filters.find((filter) => filter.id === 'blockchain')
                        ? filters.find((filter) => filter.id === 'blockchain')?.value
                        : []
                    }
                  />
                </Col>
                {Object.keys(collectionOptions).length > 1 && (
                  <Col xs={12} md={6} className="mb-3">
                    <MultiSelectComponent
                      placeholder="Select Collection(s)…"
                      options={collectionOptions}
                      onChange={onCollectionFilter}
                      values={
                        filters.find((filter) => filter.id === 'collection')
                          ? filters.find((filter) => filter.id === 'collection')?.value
                          : []
                      }
                    />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </Col>
        <Col>
          <div className="d-flex justify-content-end">
            <SelectComponent
              options={pageSizeOptions}
              onChange={handlePageSizeChange}
              value={{ value: pageSize.toString(), label: pageSize.toString() }}
              color={SelectColors.Blue}
            />
          </div>
        </Col>
      </Row>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  className={`${column.id}-table`}
                  {...column.getHeaderProps(column.getSortByToggleProps({ title: undefined }))}
                >
                  <div className="d-flex justify-content-center">
                    <div>{column.render('Header')}</div>

                    {column.canSort && (
                      <div
                        className={`nowrap ms-1 sort-arrows${
                          // eslint-disable-next-line no-nested-ternary
                          column.isSorted ? (column.isSortedDesc ? ' sorted-desc' : ' sorted-asc') : ''
                        }`}
                      >
                        <span className="desc">↑</span>
                        <span className="asc">↓</span>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody {...getTableBodyProps()}>
          <tr className="spacer" />
          {page.map((row) => {
            prepareRow(row);
            return <CollapsableRow row={row} key={`row_${row.id}`} />;
          })}
        </tbody>
      </table>
      <Row className="mt-3">
        <Col xs={12} md={6}>
          <div className="page-counter">{summaryText}</div>
        </Col>
        <Col xs={12} md={6}>
          <ReactPaginate
            className="pagination-selector"
            breakLabel="..."
            nextLabel=">"
            onPageChange={handlePageClick}
            pageRangeDisplayed={3}
            marginPagesDisplayed={1}
            pageCount={pageCount}
            previousLabel="<"
            forcePage={pageIndex}
          />
        </Col>
      </Row>
    </div>
  ) : (
    <NoCollectiblesComponent noCollectiblesType={NoCollectiblesType.Transactions} />
  );
}
